<template>
    <b-card no-body class="nsp-card">
        <b-card-header class="d-flex align-items-center">
            <span class="nsp-title">{{ $t('NSP_RECUP') }}</span>
            <b-badge class="ml-auto" :variant="ac.recoupOn ? 'success' : 'secondary'">
                {{ ac.recoupOn ? $t('NSP_RUNNING') : $t('NSP_STOPPED') }}
            </b-badge>
        </b-card-header>

        <b-card-body>
            <div class="d-flex align-items-center mb-3">
                <b-button
                    :variant="ac.recoupUserOn ? 'success' : 'outline-secondary'"
                    size="lg"
                    class="nsp-power"
                    v-on:click="$emit('power', !ac.recoupUserOn)">
                    <i class="icon-power"></i>
                </b-button>
                <div class="ml-3">
                    <div class="small text-muted">{{ $t('NSP_REQUESTED') }}</div>
                    <div>{{ ac.recoupUserOn ? $t('NSP_ON') : $t('NSP_OFF') }}</div>
                </div>
                <div class="ml-4">
                    <div class="small text-muted">{{ $t('NSP_DEVICE_REPORTS') }}</div>
                    <div>{{ ac.recoupOn ? $t('NSP_ON') : $t('NSP_OFF') }} &middot; {{ ac.recoupMode }}</div>
                </div>
            </div>

            <div class="row align-items-center">
                <div class="col-5 text-center" v-on:mouseup="pushTemp" v-on:touchend="pushTemp">
                    <round-slider
                        v-model="roomTemp"
                        start-angle="0"
                        end-angle="+180"
                        line-cap="round"
                        min="15"
                        max="30"
                        step="1"
                        radius="80"/>
                    <div class="small text-muted">{{ $t('NSP_ROOM_SETPOINT') }}</div>
                </div>
                <div class="col-7">
                    <div class="nsp-flow">
                        <span class="text-muted">{{ $t('NSP_OUTSIDE') }}</span>
                        <strong>{{ ac.outsideTemp }}&#8451;</strong>
                        <span class="nsp-arrow">&rarr;</span>
                        <strong>{{ ac.insideTemp }}&#8451;</strong>
                        <span class="text-muted">{{ $t('NSP_INSIDE') }}</span>
                    </div>

                    <b-button-group class="w-100 mt-3">
                        <b-button
                            :variant="ac.recoupSeason === 'winter' ? 'info' : 'outline-secondary'"
                            v-on:click="$emit('season', 'winter')">{{ $t('NSP_WINTER') }}</b-button>
                        <b-button
                            :variant="ac.recoupSeason === 'summer' ? 'warning' : 'outline-secondary'"
                            v-on:click="$emit('season', 'summer')">{{ $t('NSP_SUMMER') }}</b-button>
                    </b-button-group>

                    <div class="mt-3">
                        <b-badge :variant="ac.bypassOn ? 'primary' : 'light'" class="mr-2">
                            {{ ac.bypassOn ? $t('NSP_BYPASS_OPEN') : $t('NSP_HEAT_RECOVERY') }}
                        </b-badge>
                        <b-badge v-if="ac.recoupFilter" variant="danger">{{ $t('NSP_FILTER_ALARM') }}</b-badge>
                    </div>
                </div>
            </div>

            <div class="mt-3">
                <b-button-group class="w-100">
                    <b-button
                        :variant="isAuto ? 'success' : 'outline-secondary'"
                        v-on:click="$emit('mode', 'Automatic')">{{ $t('NSP_MODE_AUTOMATIC') }}</b-button>
                    <b-button
                        :variant="isAuto ? 'outline-secondary' : 'warning'"
                        v-on:click="$emit('mode', 'Manual')">{{ $t('NSP_MODE_MANUAL') }}</b-button>
                </b-button-group>

                <div class="mt-2">
                    <div class="d-flex align-items-center small text-muted">
                        <span>{{ $t('NSP_FAN_SUPPLY') }}</span>
                        <span class="ml-auto">
                            {{ supply }}% &middot; {{ ac.supplyFan }}% {{ $t('NSP_APPLIED') }}
                        </span>
                    </div>
                    <b-form-input
                        type="range"
                        v-model.number="supply"
                        :min="ac.fanMin"
                        :max="ac.fanMax"
                        step="1"
                        :disabled="isAuto"
                        v-on:change="pushFans"/>
                </div>

                <div class="mt-2">
                    <div class="d-flex align-items-center small text-muted">
                        <span>{{ $t('NSP_FAN_RETURN') }}</span>
                        <span class="ml-auto">
                            {{ ret }}% &middot; {{ ac.returnFan }}% {{ $t('NSP_APPLIED') }}
                        </span>
                    </div>
                    <b-form-input
                        type="range"
                        v-model.number="ret"
                        :min="ac.fanMin"
                        :max="ac.fanMax"
                        step="1"
                        :disabled="isAuto"
                        v-on:change="pushFans"/>
                </div>

                <div class="small text-muted mt-1">
                    {{ isAuto ? $t('NSP_FAN_AUTO_HINT') : $t('NSP_FAN_MANUAL_HINT') }}
                </div>
            </div>

            <div class="nsp-diag small text-muted mt-3">
                <div>
                    {{ $t('NSP_BYPASS_RELAY') }}:
                    <strong :class="{ 'text-danger': ac.bypassRelay !== ac.bypassOn }">
                        {{ ac.bypassRelay ? $t('NSP_ON') : $t('NSP_OFF') }}
                    </strong>
                    <span class="ml-1">
                        ({{ $t('NSP_STATUS_BIT') }}: {{ ac.bypassOn ? $t('NSP_ON') : $t('NSP_OFF') }})
                    </span>
                </div>
                <div>
                    {{ $t('NSP_FANS') }}:
                    {{ $t('NSP_SUPPLY') }} {{ ac.supplyFan }}% / {{ $t('NSP_RETURN') }} {{ ac.returnFan }}%
                </div>
            </div>
        </b-card-body>
    </b-card>
</template>

<script>
export default
{
    name: 'RecuperatorCard',
    props:
    {
        ac:
        {
            type: Object,
            required: true,
        },
    },
    data()
    {
        return {
            roomTemp: this.ac.roomTemp,
            supply: this.ac.fanSupplySet,
            ret: this.ac.fanReturnSet,
        };
    },
    computed:
    {
        /**
         * What we asked the unit to be, not what it reports. The buttons have to
         * follow the click straight away, and the unit only confirms on the next
         * read of the status register.
         */
        isAuto()
        {
            return (this.ac.recoupWantedMode !== 'Manual');
        },
    },
    watch:
    {
        'ac.roomTemp': function acRoomTemp(value)
        {
            this.roomTemp = value;
        },
        'ac.fanSupplySet': function acFanSupplySet(value)
        {
            this.supply = value;
        },
        'ac.fanReturnSet': function acFanReturnSet(value)
        {
            this.ret = value;
        },
    },
    methods:
    {
        pushTemp()
        {
            const me = this;

            if (me.roomTemp !== me.ac.roomTemp)
            {
                me.$emit('roomtemp', me.roomTemp);
            }
        },
        // on change rather than on input, so dragging sends one write and not forty
        pushFans()
        {
            const me = this;

            me.$emit('fans', { supply: me.supply, ret: me.ret });
        },
    },
};
</script>
